import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { BillingProvider } from './contexts/BillingContext';
import { ConsultantBrandingProvider } from './contexts/ConsultantBrandingContext';
import { ThemeProvider, useThemeMode } from './contexts/ThemeContext';
import { lightTheme, darkTheme } from './theme/theme';

/**
 * ThemeSyncComponent - Syncs theme preference when user logs in
 * Only syncs once per session to prevent overwriting user changes
 */
function ThemeSyncComponent() {
  const { user } = useAuth();
  const { loadThemeFromUser } = useThemeMode();
  const hasSyncedRef = React.useRef(false);

  useEffect(() => {
    // Only sync theme from backend if we haven't synced yet this session
    // and the user has a theme preference
    if (user?.theme_preference && !hasSyncedRef.current) {
      console.log('[Theme Sync] Loading theme from user profile:', user.theme_preference);
      loadThemeFromUser(user.theme_preference);
      hasSyncedRef.current = true;
    }
    
    // Reset sync flag when user logs out
    if (!user) {
      hasSyncedRef.current = false;
    }
  }, [user, loadThemeFromUser]);

  return null;
}
import Layout from './components/Layout/Layout';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import AcceptInvite from './pages/AcceptInvite';
import Dashboard from './pages/Dashboard';
import AuditSelection from './pages/AuditSelection';
import AuditSession from './pages/AuditSession';
import AuditAnalysis from './pages/AuditAnalysis';
import CompanySettings from './pages/CompanySettings';
import CompanyManagement from './pages/CompanyManagement';
import AuditResultsByCompany from './pages/AuditResultsByCompany';
import CompanySelector from './pages/CompanySelector';
import CompanyDashboard from './pages/CompanyDashboard';
import AuditManagement from './pages/AuditManagement';
import AdminPortal from './components/AdminPortal';
import AITemplateBuilder from './pages/AITemplateBuilder';
import ExcelTemplateImport from './pages/ExcelTemplateImport';
import TemplateEditorPage from './pages/TemplateEditorPage';
import BillingSuccess from './pages/BillingSuccess';
import BillingCancel from './pages/BillingCancel';
import ProtectedRoute from './components/Auth/ProtectedRoute';
// Legal & Compliance Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import CookieConsent from './components/CookieConsent';
import MyData from './pages/MyData';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ActivityLog from './pages/ActivityLog';
import Settings from './pages/Settings';
import CommunityTemplates from './pages/CommunityTemplates';
import KPIDashboard from './pages/KPIDashboard';

/**
 * AppContent - Inner component that uses theme context
 */
function AppContent() {
  const { mode } = useThemeMode();
  const theme = mode === 'dark' ? darkTheme : lightTheme;

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ThemeSyncComponent />
        <BillingProvider>
          <CompanyProvider>
            <ConsultantBrandingProvider>
              <Router>
                <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/accept-invite" element={<AcceptInvite />} />
                    
                    {/* Legal & Compliance Pages - wrapped in Layout for header/breadcrumbs */}
                    <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
                    <Route path="/terms-of-service" element={<Layout><TermsOfService /></Layout>} />
                    <Route path="/cookie-policy" element={<Layout><CookiePolicy /></Layout>} />
                    
                    {/* User Data Management (Protected) */}
                    <Route path="/my-data" element={
                      <ProtectedRoute>
                        <Layout>
                          <MyData />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    {/* Activity Log (Protected) */}
                    <Route path="/activity-log" element={
                      <ProtectedRoute>
                        <Layout>
                          <ActivityLog />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    {/* Settings (Protected) */}
                    <Route path="/settings" element={
                      <ProtectedRoute>
                        <Layout>
                          <Settings />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    {/* Main Consultant Dashboard */}
                    <Route path="/dashboard" element={
                      <ProtectedRoute>
                        <Layout>
                          <Dashboard />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    {/* Company Selection and Management */}
                    <Route path="/companies" element={
                      <ProtectedRoute>
                        <Layout>
                          <CompanySelector />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    <Route path="/company/:companyId" element={
                      <ProtectedRoute>
                        <Layout>
                          <CompanyDashboard />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    <Route path="/company/:companyId/settings" element={
                      <ProtectedRoute>
                        <Layout>
                          <CompanySettings />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    <Route path="/company/:companyId/audits" element={
                      <ProtectedRoute>
                        <Layout>
                          <AuditManagement />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    <Route path="/company/:companyId/kpis" element={
                      <ProtectedRoute>
                        <Layout>
                          <KPIDashboard />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    {/* Audit Flow */}
                    <Route path="/company/:companyId/audit/start" element={
                      <ProtectedRoute>
                        <Layout>
                          <AuditSelection />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    <Route path="/audit-session/:sessionId" element={
                      <ProtectedRoute>
                        <Layout>
                          <AuditSession />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    <Route path="/audit-analysis/:sessionId" element={
                      <ProtectedRoute>
                        <Layout>
                          <AuditAnalysis />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    {/* AI Template Builder */}
                    <Route path="/ai-builder" element={
                      <ProtectedRoute>
                        <Layout>
                          <AITemplateBuilder />
                        </Layout>
                      </ProtectedRoute>
                    } />

                    {/* Template Import (Standalone / Beta) */}
                    <Route path="/ai-builder/excel-import" element={
                      <ProtectedRoute>
                        <Layout>
                          <ExcelTemplateImport />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    {/* Community Templates */}
                    <Route path="/community-templates" element={
                      <ProtectedRoute>
                        <Layout>
                          <CommunityTemplates />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    {/* Admin Portal */}
                    <Route path="/admin/*" element={
                      <ProtectedRoute>
                        <Layout>
                          <AdminPortal />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    {/* Audit Results */}
                    <Route path="/audit-results" element={
                      <ProtectedRoute>
                        <Layout>
                          <AuditResultsByCompany />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    {/* Billing Routes */}
                    <Route path="/billing/success" element={
                      <ProtectedRoute>
                        <Layout>
                          <BillingSuccess />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    <Route path="/billing/cancel" element={
                      <ProtectedRoute>
                        <Layout>
                          <BillingCancel />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    
                    {/* Legacy routes for backward compatibility */}
                    <Route path="/audit-selection" element={<Navigate to="/companies" replace />} />
                    <Route path="/company-settings" element={<Navigate to="/admin/settings" replace />} />
                    
                    {/* Redirect unknown routes to landing page */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  
                  {/* Cookie Consent Banner - shows on all pages */}
                  <CookieConsent />
                </Box>
              </Router>
            </ConsultantBrandingProvider>
          </CompanyProvider>
        </BillingProvider>
      </AuthProvider>
    </MuiThemeProvider>
  );
}

/**
 * Main App component with ThemeProvider wrapper
 */
function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
